import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface NewsItem {
  id: string;
  title: string;
  source: string;
  date: string;
  category: string;
  summary: string;
  link: string;
  icon: string;
}

export default function NewsTracking() {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isSubscribeOpen, setIsSubscribeOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const categories = [
    { id: 'all', name: t('news.categories.all'), icon: 'ri-global-line' },
    { id: 'ind', name: 'IND', icon: 'ri-government-line' },
    { id: 'coa', name: 'COA', icon: 'ri-building-line' },
    { id: 'municipality', name: t('news.categories.municipality'), icon: 'ri-community-line' },
    { id: 'media', name: t('news.categories.media'), icon: 'ri-newspaper-line' },
    { id: 'policy', name: t('news.categories.policy'), icon: 'ri-file-list-3-line' },
  ];

  const newsItems: NewsItem[] = [
    {
      id: '1',
      title: t('news.items.ind1.title'),
      source: 'IND',
      date: '2024-01-15',
      category: 'ind',
      summary: t('news.items.ind1.summary'),
      link: '#',
      icon: 'ri-government-line',
    },
    {
      id: '2',
      title: t('news.items.coa1.title'),
      source: 'COA',
      date: '2024-01-14',
      category: 'coa',
      summary: t('news.items.coa1.summary'),
      link: '#',
      icon: 'ri-building-line',
    },
    {
      id: '3',
      title: t('news.items.municipality1.title'),
      source: t('news.items.municipality1.source'),
      date: '2024-01-13',
      category: 'municipality',
      summary: t('news.items.municipality1.summary'),
      link: '#',
      icon: 'ri-community-line',
    },
    {
      id: '4',
      title: t('news.items.media1.title'),
      source: 'NOS',
      date: '2024-01-12',
      category: 'media',
      summary: t('news.items.media1.summary'),
      link: '#',
      icon: 'ri-newspaper-line',
    },
    {
      id: '5',
      title: t('news.items.policy1.title'),
      source: 'Overheid.nl',
      date: '2024-01-11',
      category: 'policy',
      summary: t('news.items.policy1.summary'),
      link: '#',
      icon: 'ri-file-list-3-line',
    },
    {
      id: '6',
      title: t('news.items.ind2.title'),
      source: 'IND',
      date: '2024-01-10',
      category: 'ind',
      summary: t('news.items.ind2.summary'),
      link: '#',
      icon: 'ri-government-line',
    },
  ];

  const filteredNews = selectedCategory === 'all' 
    ? newsItems 
    : newsItems.filter(item => item.category === selectedCategory);

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    const formData = new URLSearchParams();
    formData.append('email', email);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5656gsg267ndt4ac5q0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setEmail('');
        setTimeout(() => {
          setSubmitStatus('idle');
          setIsSubscribeOpen(false);
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="news" className="py-24 bg-gradient-to-br from-gray-50 to-teal-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-orange-500 rounded-2xl mb-6">
            <i className="ri-newspaper-line text-3xl text-white"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {t('news.title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-8">
            {t('news.subtitle')}
          </p>
          <button
            onClick={() => setIsSubscribeOpen(true)}
            className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-lg font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            <i className="ri-mail-line mr-2"></i>
            {t('news.subscribe')}
          </button>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`flex items-center px-6 py-3 rounded-full font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                selectedCategory === category.id
                  ? 'bg-gradient-to-r from-teal-600 to-teal-700 text-white shadow-lg'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <i className={`${category.icon} mr-2`}></i>
              {category.name}
            </button>
          ))}
        </div>

        {/* News Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNews.map((news) => (
            <article
              key={news.id}
              className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
                      <i className={`${news.icon} text-xl text-teal-600`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{news.source}</p>
                      <p className="text-xs text-gray-500">{news.date}</p>
                    </div>
                  </div>
                  <span className="px-3 py-1 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
                    {t('news.new')}
                  </span>
                </div>

                <h3 className="text-lg font-bold text-gray-900 mb-3 group-hover:text-teal-600 transition-colors line-clamp-2">
                  {news.title}
                </h3>

                <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                  {news.summary}
                </p>

                <a
                  href={news.link}
                  className="inline-flex items-center text-teal-600 font-semibold text-sm hover:text-teal-700 transition-colors cursor-pointer"
                >
                  {t('news.readMore')}
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                </a>
              </div>
            </article>
          ))}
        </div>

        {/* Info Box */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-8 border-l-4 border-teal-600">
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-information-line text-2xl text-teal-600"></i>
            </div>
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {t('news.infoBox.title')}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {t('news.infoBox.description')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscribe Modal */}
      {isSubscribeOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative">
            <button
              onClick={() => setIsSubscribeOpen(false)}
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-orange-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t('news.modal.title')}
              </h3>
              <p className="text-gray-600">
                {t('news.modal.description')}
              </p>
            </div>

            <form id="newsletter-form" data-readdy-form onSubmit={handleSubscribe} className="space-y-4">
              <div>
                <label htmlFor="subscribe-email" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('news.modal.emailLabel')}
                </label>
                <input
                  type="email"
                  id="subscribe-email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                  placeholder={t('news.modal.emailPlaceholder')}
                />
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-checkbox-circle-line mr-2"></i>
                  {t('news.modal.successMessage')}
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  {t('news.modal.errorMessage')}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white px-8 py-3 rounded-lg font-semibold hover:from-teal-700 hover:to-teal-800 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin mr-2"></i>
                    {t('news.modal.subscribing')}
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <i className="ri-mail-send-line mr-2"></i>
                    {t('news.modal.subscribeButton')}
                  </span>
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
